import {sameMinute} from '~/utils/challengeSchedule';

/**
 * X239 (Grant 2026-10-29): "Move just this match, or the whole session?" In an americano with 8 or 12 players a round
 * uses 2 or 3 courts at once and each court is booked for the whole session (every round, BE10). When a club admin
 * picks a new time (or court) for a match that shares its session, the schedule dialog asks; the whole session is the
 * default. The API says when to ask (sessionMatchCount, for club admins only) and moves the whole session for
 * moveSession: true. The app's schedule_session_move.dart says exactly the same words.
 */
export const SESSION_MOVE_QUESTION = 'Move just this match, or the whole session?';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "Court 1", "Court 1 and Court 2", "Court 1, Court 2 and Court 3". */
export function listWords(names) {
  const list = (names ?? []).filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * Whether to ask: a club admin (the API only sends sessionMatchCount to them), a match that shares its session, with a
 * time already, and a pick that moves it (another time, or another court).
 */
export function sessionMoveAsked(availability, {admin, scheduledAt, areaId, pickedAt, pickedAreaId}) {
  if (!admin || (availability?.sessionMatchCount ?? 0) < 2 || !scheduledAt || !pickedAt) return false;
  if (!sameMinute(scheduledAt, pickedAt)) return true;
  return pickedAreaId != null && pickedAreaId !== (areaId ?? null);
}

/**
 * X240 (TEST_R100 M-1): the API marks a start that only the session's own bookings are in the way of (`sessionFree`,
 * club admins on a shared session only), e.g. the session an hour later on its own court. The whole session moving
 * frees them, so the picker reads such a start as free (its other rules, the time select's court, the summary, all
 * follow); moving just this match there would still be refused, so that choice is then turned off.
 */
export function withSessionMoveStarts(availability) {
  if (!availability || (availability.sessionMatchCount ?? 0) < 2) return availability;
  let changed = false;
  const days = (availability.dayList ?? []).map((d) => ({
    ...d,
    times: (d.times ?? []).map((t) => {
      if (!(t.areas ?? []).some((c) => c.sessionFree)) return t;
      changed = true;
      const areas = t.areas.map((c) => (c.sessionFree && c.status === 'taken' ? {...c, status: 'free', struck: false, label: null} : c));
      return {...t, areas, freeCount: areas.filter((c) => ['free', 'open', 'mine'].includes(c.status)).length};
    }),
  }));
  return changed ? {...availability, dayList: days} : availability;
}

/** X240: whether the picked start fits only when the whole session moves (its cell is `sessionFree`). */
export function pickNeedsWholeSession(availability, startsAt, areaId) {
  if (!startsAt || !areaId) return false;
  const at = new Date(startsAt).getTime();
  for (const d of availability?.dayList ?? []) {
    for (const t of d.times ?? []) {
      if (new Date(t.startsAt).getTime() !== at) continue;
      return !!(t.areas ?? []).find((c) => c.areaId === areaId)?.sessionFree;
    }
  }
  return false;
}

/** X240: "Move just this match" when only the whole session fits the picked start. */
export const ONLY_WHOLE_SESSION = 'Not at this time: the other rounds keep the session where it is, so only the whole session fits.';

/**
 * The two choices, whole session first (the default). `areaName` is the court picked, `fromAreaName` the one the match
 * is on: when they differ the session's subtitle says how the courts change (they swap, or that court's matches go to
 * the new one).
 */
export function sessionMoveOptions(availability, {areaName = null, fromAreaName = null, onlyWhole = false, past = false} = {}) {
  const n = availability?.sessionMatchCount ?? 0;
  const courts = availability?.sessionCourts ?? 0;
  const names = availability?.sessionCourtNames ?? [];
  const what = courts > 0 ? `${plural(n, 'match', 'matches')} on ${plural(courts, 'court', 'courts')}` : plural(n, 'match', 'matches');
  // X244 (TEST_R102 M-A): a time that has passed books nothing, so the session's bookings are released there, never moved.
  const booking = availability?.bookingsActive && !past ? ', with its booking' : '';
  let sub = names.length ? `Every round on ${listWords(names)} moves to the new time${booking}.` : `Every round moves to the new time${booking}.`;
  if (availability?.bookingsActive && past) sub += ' The time has passed, so its bookings are released.';
  if (areaName && fromAreaName && areaName !== fromAreaName) {
    sub += names.includes(areaName) ? ` ${fromAreaName} and ${areaName} swap.` : ` ${fromAreaName}'s matches move to ${areaName}.`;
  }
  const others = n - 1;
  return [
    {value: 'session', title: `Move the whole session (${what})`, subtitle: sub},
    onlyWhole
      ? {value: 'match', title: 'Move just this match', subtitle: ONLY_WHOLE_SESSION, disabled: true}
      : {value: 'match', title: 'Move just this match', subtitle: `The other ${plural(others, 'match stays', 'matches stay')} where ${others === 1 ? 'it is' : 'they are'}.`},
  ];
}

/** The request's flag: only when asked and the whole session is chosen; left out otherwise (the request as before). */
export function sessionMoveRequest(asked, scope) {
  return asked && scope !== 'match' ? {moveSession: true} : {};
}
